import type { Booking } from "../../types/booking";
import type { BookingCommercialInvoiceAnalysisSummary } from "../../types/booking";
import type {
  CommercialInvoiceAnalysisResult,
  CommercialInvoiceDeepCheck,
  CommercialInvoiceDeepCheckExtracted,
  CommercialInvoiceDeepCheckComparison,
  CommercialInvoiceDocumentType,
} from "./types";
import { crossCheckCommercialInvoiceWithBooking } from "./bookingCrossCheck";
import {
  cacheRowToAnalysisResult,
  commercialInvoiceValidityColor,
  normalizeCommercialInvoiceAnalysisEmbed,
  type CommercialInvoiceAnalysisCacheRow,
} from "./commercialInvoiceAnalysisCache";

export type ValidityScoreSource = "auto" | "ai";

/** Fields the AI deep check tries to extract — used for AI completeness score. */
const DEEP_CHECK_EXTRACTED_KEYS: (keyof CommercialInvoiceDeepCheckExtracted)[] = [
  "sellerName",
  "sellerAddress",
  "buyerName",
  "buyerAddress",
  "buyerVat",
  "invoiceNumber",
  "invoiceDate",
  "incoterm",
  "currency",
  "totalAmount",
  "grossWeight",
  "netWeight",
  "countryOfOrigin",
];

export interface CommercialInvoiceValidityBreakdown {
  /** Passes step 1: not classified as proforma / PI. */
  isNonProforma: boolean;
  nonProformaDetail: string;
  nonProformaSource: ValidityScoreSource;
  /** Step 2: required fields present (0–100). */
  completenessScore: number;
  completenessSource: ValidityScoreSource;
  completenessDetail: string;
  /** Step 3: booking vs invoice (0–100). */
  matchScore: number | null;
  matchSource: ValidityScoreSource | null;
  matchDetail: string;
  /** Saved AI deep check was used for any score above. */
  hasDeepCheck: boolean;
}

export function isNonProformaInvoice(
  documentType: CommercialInvoiceDocumentType | string,
  proformaLikelihood: number
): boolean {
  if (documentType === "proforma_invoice") return false;
  if (proformaLikelihood >= 50) return false;
  return true;
}

export function computeBookingMatchScore(
  analysis: CommercialInvoiceAnalysisResult,
  booking: Booking
): { score: number; comparedCount: number; matchCount: number } {
  const crossCheck = crossCheckCommercialInvoiceWithBooking(analysis, booking);
  const comparable = crossCheck.rows.filter((row) => row.status !== "missing_booking");
  if (comparable.length === 0) {
    return { score: 100, comparedCount: 0, matchCount: 0 };
  }
  const matchCount = comparable.filter((row) => row.status === "match").length;
  return {
    score: Math.round((matchCount / comparable.length) * 100),
    comparedCount: comparable.length,
    matchCount,
  };
}

export function computeDeepCheckCompletenessScore(
  extracted: CommercialInvoiceDeepCheckExtracted | undefined
): { score: number; filledCount: number; totalCount: number } | null {
  if (!extracted) return null;

  const filledCount = DEEP_CHECK_EXTRACTED_KEYS.filter((key) => {
    const value = extracted[key];
    return value != null && String(value).trim() !== "";
  }).length;

  const totalCount = DEEP_CHECK_EXTRACTED_KEYS.length;
  return {
    score: Math.round((filledCount / totalCount) * 100),
    filledCount,
    totalCount,
  };
}

export function computeDeepCheckMatchScore(
  comparisons: CommercialInvoiceDeepCheckComparison[] | undefined
): { score: number; comparedCount: number; matchCount: number } | null {
  if (!comparisons?.length) return null;

  const comparable = comparisons.filter((row) => row.status !== "missing_booking");
  if (comparable.length === 0) return null;

  const matchCount = comparable.filter((row) => row.status === "match").length;
  return {
    score: Math.round((matchCount / comparable.length) * 100),
    comparedCount: comparable.length,
    matchCount,
  };
}

function applyDeepCheckScores(
  analysis: CommercialInvoiceAnalysisResult,
  booking: Booking | null | undefined,
  base: Omit<
    CommercialInvoiceValidityBreakdown,
    "hasDeepCheck" | "completenessSource" | "completenessDetail" | "matchSource" | "nonProformaSource"
  >
): CommercialInvoiceValidityBreakdown {
  const deepCheck = analysis.deepCheck;
  if (!deepCheck?.available) {
    return {
      ...base,
      nonProformaSource: "auto",
      completenessSource: "auto",
      completenessDetail: "Customs field checklist on invoice text",
      matchSource: booking ? "auto" : null,
      hasDeepCheck: false,
    };
  }

  let isNonProforma = base.isNonProforma;
  let nonProformaDetail = base.nonProformaDetail;
  let nonProformaSource: ValidityScoreSource = "auto";

  if (deepCheck.documentType != null || deepCheck.proformaLikelihood != null) {
    nonProformaSource = "ai";
    isNonProforma =
      deepCheck.documentType !== "proforma_invoice" &&
      (deepCheck.proformaLikelihood ?? 0) < 50;
    if (deepCheck.documentType === "proforma_invoice") {
      nonProformaDetail = "Classified as proforma / PI";
    } else if ((deepCheck.proformaLikelihood ?? 0) >= 50) {
      nonProformaDetail = `AI proforma signals (${Math.round(deepCheck.proformaLikelihood ?? 0)}% PI confidence)`;
    } else if (deepCheck.documentType === "uncertain") {
      nonProformaDetail = "Document type uncertain — review recommended";
    } else {
      nonProformaDetail = "";
    }
  }

  let completenessScore = base.completenessScore;
  let completenessSource: ValidityScoreSource = "auto";
  let completenessDetail = "Customs field checklist on invoice text";

  const aiCompleteness = computeDeepCheckCompletenessScore(deepCheck.extracted);
  if (aiCompleteness) {
    completenessScore = aiCompleteness.score;
    completenessSource = "ai";
    completenessDetail = `${aiCompleteness.filledCount}/${aiCompleteness.totalCount} key fields extracted`;
  }

  let matchScore = base.matchScore;
  let matchSource: ValidityScoreSource | null = booking ? "auto" : null;
  let matchDetail = base.matchDetail;

  const aiMatch = computeDeepCheckMatchScore(deepCheck.bookingComparisons);
  if (aiMatch) {
    matchScore = aiMatch.score;
    matchSource = "ai";
    matchDetail = `${aiMatch.matchCount}/${aiMatch.comparedCount} booking fields matched`;
  }

  return {
    isNonProforma,
    nonProformaDetail,
    nonProformaSource,
    completenessScore,
    completenessSource,
    completenessDetail,
    matchScore,
    matchSource,
    matchDetail,
    hasDeepCheck: true,
  };
}

export function getCommercialInvoiceValidityBreakdown(
  analysis: CommercialInvoiceAnalysisResult,
  booking?: Booking | null
): CommercialInvoiceValidityBreakdown {
  const isNonProforma = isNonProformaInvoice(
    analysis.documentType,
    analysis.proformaLikelihood
  );

  let nonProformaDetail = "";
  if (analysis.documentType === "proforma_invoice") {
    nonProformaDetail = "Classified as proforma / PI — not valid for customs";
  } else if (analysis.proformaLikelihood >= 50) {
    nonProformaDetail = `Proforma signals (${analysis.proformaLikelihood}% PI confidence)`;
  } else if (analysis.documentType === "uncertain") {
    nonProformaDetail = "Document type uncertain — review recommended";
  }

  let matchScore: number | null = null;
  let matchDetail = "Booking data not available";
  if (booking) {
    const match = computeBookingMatchScore(analysis, booking);
    matchScore = match.score;
    matchDetail =
      match.comparedCount === 0
        ? "No booking fields to compare"
        : `${match.matchCount}/${match.comparedCount} booking fields matched on invoice`;
  }

  return applyDeepCheckScores(analysis, booking, {
    isNonProforma,
    nonProformaDetail,
    completenessScore: analysis.completenessScore,
    matchScore,
    matchDetail,
  });
}

export function getAnalysisResultFromBookingEmbed(
  booking: Booking
): CommercialInvoiceAnalysisResult | null {
  const embed = booking.commercial_invoice_analysis;
  if (!embed) return null;

  const row = normalizeCommercialInvoiceAnalysisEmbed(
    embed as CommercialInvoiceAnalysisCacheRow
  );
  if (!row?.result || typeof row.result !== "object") return null;

  return cacheRowToAnalysisResult(row);
}

export function getValidityBreakdownFromBooking(
  booking: Booking
): CommercialInvoiceValidityBreakdown | null {
  const summary = normalizeBookingAnalysisSummary(booking.commercial_invoice_analysis);
  if (!summary) return null;

  const fullResult = getAnalysisResultFromBookingEmbed(booking);
  if (fullResult) {
    return getCommercialInvoiceValidityBreakdown(fullResult, booking);
  }

  const isNonProforma = isNonProformaInvoice(
    summary.document_type,
    summary.proforma_likelihood
  );

  return {
    isNonProforma,
    nonProformaDetail: isNonProforma ? "" : "Proforma / PI detected",
    nonProformaSource: "auto" as const,
    completenessScore: summary.completeness_score,
    completenessSource: "auto" as const,
    completenessDetail: "Customs field checklist on invoice text",
    matchScore: null,
    matchSource: null,
    matchDetail: "Run analysis for booking match score",
    hasDeepCheck: false,
  };
}

export function normalizeBookingAnalysisSummary(
  value:
    | BookingCommercialInvoiceAnalysisSummary
    | BookingCommercialInvoiceAnalysisSummary[]
    | null
    | undefined
): BookingCommercialInvoiceAnalysisSummary | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

export { commercialInvoiceValidityColor as validityScoreColor };
